import { Icon, PillBadge } from '@platform/components';
import { useState, useRef } from 'react';
import ContentPanel from './ContentPanel';
import { H2, Description } from './utils';

import ProjectCardVisual1 from './images/project-card-visual1.png';
import ProjectCardVisual2 from './images/project-card-visual2.png';
import ProjectCardVisual3 from './images/project-card-visual3.png';
import ProjectCardVisual4 from './images/project-card-visual4.png';
import ProjectCardVisual5 from './images/project-card-visual5.png';
import ProjectCardVisual6 from './images/project-card-visual6.png';
import cx from 'classix';

const CARD_SPACING = 32;

const TESTIMONIAL_CARD_DATA = [
  {
    bannerImage: ProjectCardVisual1,
    header: 'Faces Up Uganda',
    description:
      'Worked with a team of SAP consultants to turn their manual monitoring and evaluation tool into a digital visualizer',
    categoryText: 'Data Analysis',
    locationText: 'Uganda',
  },
  {
    bannerImage: ProjectCardVisual2,
    header: 'Yolele Foods',
    description:
      "Worked with a Reckitt consultant to learn how to prioritize the multiple objectives they faced and best allocate their team's time",
    categoryText: 'Operations',
    locationText: 'United States',
  },
  {
    bannerImage: ProjectCardVisual3,
    header: 'Kadana Foundation',
    description:
      'Worked with an EY Coach to build an engagement plan for stakeholders involved in the growth of their development framework',
    categoryText: 'Business Development',
    locationText: 'Kenya',
  },
  {
    bannerImage: ProjectCardVisual4,
    header: 'Circular Cities Asia',
    description:
      'Worked with an SAP consultant to identify key customer segments, develop its value proposition canvas, and design targeted pitch decks',
    categoryText: 'Marketing',
    locationText: 'New Zealand',
  },
  {
    bannerImage: ProjectCardVisual5,
    header: 'LandPrint',
    description:
      'Worked with an EY coach to align its technology with new ESG reporting requirements.',
    categoryText: 'Technology',
    locationText: 'Brazil',
  },
  {
    bannerImage: ProjectCardVisual6,
    header: 'Safeplan Uganda',
    description:
      'Worked with SAP consultants to select, implement, and train its team on a new financial management software.',
    categoryText: 'Accounting',
    locationText: 'Uganda',
  },
] as const;

const TestimonialCards = () => {
  // Which card is currently displayed aligned with the left side of the page content.
  const [index, setIndex] = useState(0);
  const cardContainerRef = useRef<HTMLDivElement | null>(null);

  const scrollToCard = (direction: 'left' | 'right') => {
    if (!cardContainerRef.current) return;

    // Determine card width by measuring the width of the first child card. This assumes all cards have the same width.
    const cardWidth = cardContainerRef.current.children[0].clientWidth + CARD_SPACING;

    // Update index based on direction
    let newIndex = index;
    if (direction === 'left') {
      newIndex = Math.max(0, index - 1);
    } else {
      newIndex = Math.min(TESTIMONIAL_CARD_DATA.length - 1, index + 1);
    }
    setIndex(newIndex);

    cardContainerRef.current.style.translate = `-${newIndex * cardWidth}px`;
  };

  return (
    <div className='inline-flex flex-col'>
      <div
        ref={cardContainerRef}
        className='grid grid-cols-[repeat(6,288px)] sm:grid-cols-[repeat(6,384px)] gap-8 transition-transform'
      >
        {TESTIMONIAL_CARD_DATA.map((data, index) => (
          <TestimonialCard
            key={index}
            bannerImage={data.bannerImage}
            header={data.header}
            description={data.description}
            categoryText={data.categoryText}
            locationText={data.locationText}
          />
        ))}
      </div>
      <div className='mt-8 flex gap-4 sm:gap-8'>
        <LeftRightButton type='left' onClick={() => scrollToCard('left')} disabled={index === 0} />
        <LeftRightButton
          type='right'
          onClick={() => scrollToCard('right')}
          disabled={index === TESTIMONIAL_CARD_DATA.length - 1}
        />
      </div>
    </div>
  );
};

const LeftRightButton = ({
  type,
  onClick,
  disabled,
}: {
  type: 'left' | 'right';
  onClick: React.MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
}) => {
  return (
    <div
      className={cx(
        'flex items-center justify-center rounded-full border-[1px] border-gray-100 text-gray-400 w-12 h-12 sm:w-14 sm:h-14 cursor-pointer',
        disabled && '!cursor-default opacity-50',
      )}
      onClick={disabled ? undefined : onClick}
    >
      <div className='sm:hidden'>
        <Icon type={type === 'left' ? 'arrow-left' : 'arrow-right'} size='20' />
      </div>
      <div className='hidden sm:block'>
        <Icon type={type === 'left' ? 'arrow-left' : 'arrow-right'} size='24' />
      </div>
    </div>
  );
};

const TestimonialCard = ({
  bannerImage,
  header,
  description,
  categoryText,
  locationText,
}: {
  bannerImage: string;
  header: string;
  description: string;
  categoryText: string;
  locationText: string;
}) => {
  return (
    <div className='flex flex-col min-w-[288px] max-w-[384px]'>
      <div>
        <img className='w-full' src={bannerImage} />
      </div>
      <div className='flex flex-col flex-grow border border-gray-100 rounded-b-xl px-5 py-6'>
        <div>
          <div className='text-3xl font-semibold leading-9 text-gray-900'>{header}</div>
          <div className='mt-4 mb-8 text-lg leading-7 text-gray-900'>{description}</div>
        </div>
        <div className='mt-auto flex gap-2'>
          <PillBadge color='blue' variant='plain' size='lg'>
            {categoryText}
          </PillBadge>
          <PillBadge color='success-25' variant='plain' size='lg'>
            {locationText}
          </PillBadge>
        </div>
      </div>
    </div>
  );
};

const TestimonialCardsSection = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  return (
    <>
      <ContentPanel noBottomPadding>
        <H2>{title}</H2>
        <Description>{description}</Description>

        <TestimonialCards />
      </ContentPanel>
    </>
  );
};

export default TestimonialCardsSection;
